import { DetailPageAccessMode } from '@app/detailPageLayout.models';
import { FETCH_QUERIES_NAME } from '@common/constants';
import { LabelWithMessage } from '@common/LabelWithMessage';
import { useProjectId } from '@config/config.hooks';
import { FormDrawingSelect } from '@drawing/FormDrawingSelect';
import { useFetchDrawingSearch } from '@drawing/projectDrawingRegister.queries';
import { Attachments } from '@file/Attachments';
import { EProjectFileType } from '@file/projectFile/projectFile.constants';
import { useFetchAttachmentLoadingRemovalCerts } from '@file/projectFile/projectFile.queries';
import {
  FormTextField,
  requiredRule,
  FormTextarea,
  FormDatePicker,
  FormCheckbox,
  FormFieldMethods,
  Grid,
  Tooltip,
  Icon,
  ToastMessage,
  toastMessage,
} from '@gcl/react-core';
import { IMasterScheduleDetail } from '@masterSchedule/detail/masterScheduleDetail.models';
import React from 'react';
import { DownloadButton } from '@file/DownloadButton';
import { downloadAttachmentsAsync } from '@file/projectFile/projectFile.services';
import { useT4DetailFieldsEnabled } from './permitToLoadRemovalDetailPage.hooks';
import { ECreateT4FormKeys } from './permitToLoadRemoveDetailPage.constants';
import { IProjectLoadingRemovalCert, IProjectLoadingRemovalCertT3 } from './permitToLoadRemoveDetailPage.models';
import { PermitToLoadRemoveDetailT3 } from './PermitToLoadRemoveDetailT3';
import { PermitToLoadRemovalCheckList } from './PermitToLoadRemovalCheckList';

interface IIPermitToLoadRemoveDetailTabFormProps {
  formFieldMethods: FormFieldMethods;
  isReadOnly: boolean;
  mode: DetailPageAccessMode;
  loadingRemovalCertId?: number;
  loadingRemovalCertDetail?: IProjectLoadingRemovalCert;
  designModifications: IProjectLoadingRemovalCertT3[];
  masterSchedule?: IMasterScheduleDetail;
  masterScheduleId?: number;
}
export const PermitToLoadRemoveDetailTabForm = ({
  formFieldMethods,
  isReadOnly,
  mode,
  loadingRemovalCertDetail,
  loadingRemovalCertId,
  designModifications,
  masterScheduleId,
  masterSchedule,
}: IIPermitToLoadRemoveDetailTabFormProps) => {
  const projectId = useProjectId();
  const isPermitToRemoveRequired = formFieldMethods.watch(ECreateT4FormKeys.PERMIT_TO_REMOVE_REQUIRED);
  const t4DetailFieldsEnabled = useT4DetailFieldsEnabled(loadingRemovalCertDetail, masterSchedule);
  const [locationPlanCount, setLocationPlanCount] = React.useState(0);
  const [checklistCount, setChecklistCount] = React.useState(0);
  const [otherRefCount, setOtherRefCount] = React.useState(0);
  const [otherRemovalRefCount, setOtherRemovalRefCount] = React.useState(0);

  return (
    <>
      <Grid item xs={12}>
        <LabelWithMessage labelFor={ECreateT4FormKeys.LOCATION} required text="Location" />
        <FormTextField
          id={ECreateT4FormKeys.LOCATION}
          formFieldMethods={formFieldMethods}
          disabled={isReadOnly || !t4DetailFieldsEnabled.isGeneralFieldsEnabled}
          rules={requiredRule}
        />
      </Grid>
      <Grid item xs={12}>
        <LabelWithMessage labelFor={ECreateT4FormKeys.ELEMENT_OF_WORKS} required text="Element of works" />
        <FormTextField
          id={ECreateT4FormKeys.ELEMENT_OF_WORKS}
          formFieldMethods={formFieldMethods}
          disabled={isReadOnly || !t4DetailFieldsEnabled.isGeneralFieldsEnabled}
          rules={requiredRule}
        />
      </Grid>
      <Grid item xs={12}>
        <LabelWithMessage
          labelFor={ECreateT4FormKeys.EXTENT_OF_OPERATION_AND_CONSTRUCTION_ACTIVITY}
          required
          text="Extent of operation and construction activity"
        />

        <FormTextarea
          id={ECreateT4FormKeys.EXTENT_OF_OPERATION_AND_CONSTRUCTION_ACTIVITY}
          formFieldMethods={formFieldMethods}
          disabled={isReadOnly || !t4DetailFieldsEnabled.isGeneralFieldsEnabled}
          rules={requiredRule}
        />
      </Grid>
      <Grid item xs={12}>
        <LabelWithMessage
          labelFor={ECreateT4FormKeys.RELEASED_FOR_CONSTRUCTION_DRAWINGS}
          required
          text="Released for Construction Drawings"
        />
        <FormDrawingSelect
          masterScheduleId={masterSchedule?.id ?? masterScheduleId}
          keyExtractor={(item) => {
            return item.latestRfcHistory?.id ?? -1;
          }}
          formFieldMethods={formFieldMethods}
          id={ECreateT4FormKeys.RELEASED_FOR_CONSTRUCTION_DRAWINGS}
          isReadOnly={isReadOnly || !t4DetailFieldsEnabled.isGeneralFieldsEnabled}
          useFetchSearch={useFetchDrawingSearch}
          rules={requiredRule}
        />
      </Grid>
      <Grid item xs={12}>
        <PermitToLoadRemoveDetailT3
          formFieldMethods={formFieldMethods}
          loadingRemovalCertDetail={loadingRemovalCertDetail}
        />
      </Grid>
      <Grid item xs={12}>
        <LabelWithMessage labelFor={ECreateT4FormKeys.LOCATION_PLAN} text="Location Plan" />
        <Attachments
          setFileCount={setLocationPlanCount}
          className="gcl-gems-permit-to-load-remove-detail-files-display gcl-mb-md"
          mode={t4DetailFieldsEnabled.isLocationPlanEnabled ? mode : 'View'}
          id={ECreateT4FormKeys.LOCATION_PLAN}
          fileType={EProjectFileType.T4_LOCALTION_PLAN}
          uploadFileRequestParam={{ loadingRemovalCertId }}
          useFetchFiles={useFetchAttachmentLoadingRemovalCerts}
          useFetchFilesProps={{
            projectId,
            loadingRemovalCertId: loadingRemovalCertId ?? 0,
            fileType: EProjectFileType.T4_LOCALTION_PLAN,
          }}
          customHeaderButtons={[
            <Tooltip
              overlay="Download all location plans as zip"
              id="download-locationplan-attachment"
              key="download-locationplan-attachment"
            >
              {locationPlanCount > 0 ? (
                <DownloadButton
                  exportFunction={downloadAttachmentsAsync}
                  defaultExportProps={{
                    projectId,
                    loadingRemovalId: loadingRemovalCertId,
                    fileType: EProjectFileType.T4_LOCALTION_PLAN,
                  }}
                  buttonRender={(exportFile) => (
                    <Icon
                      onClick={() => exportFile()}
                      iconName="FileZip"
                      className="gcl-fc-primary-400 gcl-ic-btn-lg"
                    />
                  )}
                  onError={(errors) => {
                    toastMessage(
                      <ToastMessage
                        message={errors.errorMessage ?? 'Failed to download file'}
                        toastOptions={{ type: 'error', closeOnClick: true, autoClose: false }}
                      />,
                    );
                  }}
                />
              ) : (
                <></>
              )}
            </Tooltip>,
          ]}
          useFetchFilesQueryKey={[
            projectId,
            loadingRemovalCertId,
            FETCH_QUERIES_NAME.PROJECT_FILE_ATTACHMENTS_LOADING_REMOVAL_CERTS,
            EProjectFileType.T4_LOCALTION_PLAN,
          ]}
          formFieldMethods={formFieldMethods}
          formKeyToUpdateInCreateMode={ECreateT4FormKeys.LOCATION_PLAN}
        />
      </Grid>
      <Grid item xs={12}>
        <LabelWithMessage
          labelFor={ECreateT4FormKeys.CHECKLIST}
          text="Checklist"
          message="Where a checklist exists for the type of TW being inspected and checked, its use is mandatory. Checklist can be prepared through T4 mobile app or you can upload the here."
        />
        <PermitToLoadRemovalCheckList loadingRemovalCertId={loadingRemovalCertId} />
        <Attachments
          setFileCount={setChecklistCount}
          className="gcl-gems-permit-to-load-remove-detail-files-display gcl-mb-md"
          mode={t4DetailFieldsEnabled.isChecklistEnabled ? mode : 'View'}
          id={ECreateT4FormKeys.CHECKLIST}
          fileType={EProjectFileType.T4_CHECKLIST}
          uploadFileRequestParam={{ loadingRemovalCertId: loadingRemovalCertDetail?.id }}
          useFetchFiles={useFetchAttachmentLoadingRemovalCerts}
          useFetchFilesProps={{
            projectId,
            loadingRemovalCertId: loadingRemovalCertId ?? 0,
            fileType: EProjectFileType.T4_CHECKLIST,
          }}
          customHeaderButtons={[
            <Tooltip
              overlay="Download all checklists as zip"
              id="download-checklist-attachment"
              key="download-checklist-attachment"
            >
              {checklistCount > 0 ? (
                <DownloadButton
                  exportFunction={downloadAttachmentsAsync}
                  defaultExportProps={{
                    projectId,
                    loadingRemovalId: loadingRemovalCertId,
                    fileType: EProjectFileType.T4_CHECKLIST,
                  }}
                  buttonRender={(exportFile) => (
                    <Icon
                      onClick={() => exportFile()}
                      iconName="FileZip"
                      className="gcl-fc-primary-400 gcl-ic-btn-lg"
                    />
                  )}
                  onError={(errors) => {
                    toastMessage(
                      <ToastMessage
                        message={errors.errorMessage ?? 'Failed to download file'}
                        toastOptions={{ type: 'error', closeOnClick: true, autoClose: false }}
                      />,
                    );
                  }}
                />
              ) : (
                <></>
              )}
            </Tooltip>,
          ]}
          useFetchFilesQueryKey={[
            projectId,
            loadingRemovalCertId,
            FETCH_QUERIES_NAME.PROJECT_FILE_ATTACHMENTS_LOADING_REMOVAL_CERTS,
            EProjectFileType.T4_CHECKLIST,
          ]}
          formFieldMethods={formFieldMethods}
          formKeyToUpdateInCreateMode={ECreateT4FormKeys.CHECKLIST}
        />
      </Grid>
      <Grid item xs={12}>
        <LabelWithMessage labelFor={ECreateT4FormKeys.OTHER_REFERENCE_DOCUMENTS} text="Other reference documents" />
        <Attachments
          setFileCount={setOtherRefCount}
          className="gcl-gems-permit-to-load-remove-detail-files-display gcl-mb-md"
          mode={t4DetailFieldsEnabled.isOtherReferenceDocEnabled ? mode : 'View'}
          id={ECreateT4FormKeys.OTHER_REFERENCE_DOCUMENTS}
          fileType={EProjectFileType.T4_OTHER_REFERENCE_DOC}
          uploadFileRequestParam={{ loadingRemovalCertId: loadingRemovalCertDetail?.id }}
          useFetchFiles={useFetchAttachmentLoadingRemovalCerts}
          useFetchFilesProps={{
            projectId,
            loadingRemovalCertId: loadingRemovalCertId ?? 0,
            fileType: EProjectFileType.T4_OTHER_REFERENCE_DOC,
          }}
          customHeaderButtons={[
            <Tooltip
              overlay="Download all other reference documents as zip"
              id="download-otherRef-attachment"
              key="download-otherRef-attachment"
            >
              {otherRefCount > 0 ? (
                <DownloadButton
                  exportFunction={downloadAttachmentsAsync}
                  defaultExportProps={{
                    projectId,
                    loadingRemovalId: loadingRemovalCertId,
                    fileType: EProjectFileType.T4_OTHER_REFERENCE_DOC,
                  }}
                  buttonRender={(exportFile) => (
                    <Icon
                      onClick={() => exportFile()}
                      iconName="FileZip"
                      className="gcl-fc-primary-400 gcl-ic-btn-lg"
                    />
                  )}
                  onError={(errors) => {
                    toastMessage(
                      <ToastMessage
                        message={errors.errorMessage ?? 'Failed to download file'}
                        toastOptions={{ type: 'error', closeOnClick: true, autoClose: false }}
                      />,
                    );
                  }}
                />
              ) : (
                <></>
              )}
            </Tooltip>,
          ]}
          useFetchFilesQueryKey={[
            projectId,
            loadingRemovalCertId,
            FETCH_QUERIES_NAME.PROJECT_FILE_ATTACHMENTS_LOADING_REMOVAL_CERTS,
            EProjectFileType.T4_OTHER_REFERENCE_DOC,
          ]}
          formFieldMethods={formFieldMethods}
          formKeyToUpdateInCreateMode={ECreateT4FormKeys.OTHER_REFERENCE_DOCUMENTS}
        />
      </Grid>
      <Grid item xs={12}>
        <LabelWithMessage labelFor={ECreateT4FormKeys.PERMIT_VALID_UNTIL} text="Permit valid until" />
        <FormDatePicker
          formFieldMethods={formFieldMethods}
          id={ECreateT4FormKeys.PERMIT_VALID_UNTIL}
          disabled={isReadOnly || !t4DetailFieldsEnabled.isGeneralFieldsEnabled}
        />
      </Grid>
      <Grid item xs={12}>
        <LabelWithMessage labelFor={ECreateT4FormKeys.PERMIT_TO_REMOVE_REQUIRED} text="Permit to Remove Required?" />
        <FormCheckbox
          disabled={isReadOnly || !t4DetailFieldsEnabled.isPermitToRemoveRequiredEnable}
          id={ECreateT4FormKeys.PERMIT_TO_REMOVE_REQUIRED}
          formFieldMethods={formFieldMethods}
        />
      </Grid>
      {loadingRemovalCertDetail?.isRemovalCert && (
        <Grid item xs={12}>
          <LabelWithMessage labelFor={ECreateT4FormKeys.IS_FINAL_REMOVAL_CERT} text="Is Final Removal Certificate?" />
          <FormCheckbox
            disabled={isReadOnly || !t4DetailFieldsEnabled.isFinalRemovalCertificateEnable}
            id={ECreateT4FormKeys.IS_FINAL_REMOVAL_CERT}
            formFieldMethods={formFieldMethods}
          />
        </Grid>
      )}
      {isPermitToRemoveRequired && loadingRemovalCertDetail?.isRemovalCert && (
        <Grid item xs={12}>
          <LabelWithMessage
            labelFor={ECreateT4FormKeys.OTHER_REFERENCE_DOCUMENTS_FOR_REMOVAL}
            text="Other reference documents for Removal"
          />
          <Attachments
            setFileCount={setOtherRemovalRefCount}
            className="gcl-gems-permit-to-load-remove-detail-files-display gcl-gems-permit-to-load-remove-detail-other-ref-for-removal"
            mode={t4DetailFieldsEnabled.isOtherReferenceDocRemovalEnabled ? mode : 'View'}
            id={ECreateT4FormKeys.OTHER_REFERENCE_DOCUMENTS_FOR_REMOVAL}
            fileType={EProjectFileType.T4_REMOVE_REFERENCE_DOC}
            uploadFileRequestParam={{ loadingRemovalCertId: loadingRemovalCertDetail?.id }}
            useFetchFiles={useFetchAttachmentLoadingRemovalCerts}
            useFetchFilesProps={{
              projectId,
              loadingRemovalCertId: loadingRemovalCertId ?? 0,
              fileType: EProjectFileType.T4_REMOVE_REFERENCE_DOC,
            }}
            customHeaderButtons={[
              <Tooltip
                overlay="Download all other removal reference documents as zip"
                id="download-otherRemovalRef-attachment"
                key="download-otherRemovalRef-attachment"
              >
                {otherRemovalRefCount > 0 ? (
                  <DownloadButton
                    exportFunction={downloadAttachmentsAsync}
                    defaultExportProps={{
                      projectId,
                      loadingRemovalId: loadingRemovalCertId,
                      fileType: EProjectFileType.T4_REMOVE_REFERENCE_DOC,
                    }}
                    buttonRender={(exportFile) => (
                      <Icon
                        onClick={() => exportFile()}
                        iconName="FileZip"
                        className="gcl-fc-primary-400 gcl-ic-btn-lg"
                      />
                    )}
                    onError={(errors) => {
                      toastMessage(
                        <ToastMessage
                          message={errors.errorMessage ?? 'Failed to download file'}
                          toastOptions={{ type: 'error', closeOnClick: true, autoClose: false }}
                        />,
                      );
                    }}
                  />
                ) : (
                  <></>
                )}
              </Tooltip>,
            ]}
            useFetchFilesQueryKey={[
              projectId,
              loadingRemovalCertId,
              FETCH_QUERIES_NAME.PROJECT_FILE_ATTACHMENTS_LOADING_REMOVAL_CERTS,
              EProjectFileType.T4_REMOVE_REFERENCE_DOC,
            ]}
            formFieldMethods={formFieldMethods}
            formKeyToUpdateInCreateMode={ECreateT4FormKeys.OTHER_REFERENCE_DOCUMENTS_FOR_REMOVAL}
          />
        </Grid>
      )}
    </>
  );
};
